import React, { useEffect, useMemo, useState } from 'react';
import {
  CardNames,
  cutNftName,
  useCountDown,
  wsActionTypes,
  WSFightData,
  WSFightEnd,
  WSPlayer,
  WSRoundResults,
  WSRoundStart
} from 'shared';
import BattleActionHands from './hands';
import usePlayer from './usePlayer';
import { useWsToHttp } from '../../../../../hooks/wsToHttp/useWsToHttp';
import useAlert from '../../../../../contexts/alert';
import BattleActionInfo, { RoundResults } from './info';
import { useGlbAnimations } from './hands/useGlbAnimations';
import useWindowSize from '../../../../../hooks/useWindowSize';
import BattleActionMobile from './adaptiveViews/Mobile';
import BattleActionTablet from './adaptiveViews/Tablet';
import BattleActionPC from './adaptiveViews/PC';
import { IFightNft } from '../../../../../interfaces/FightNft';
import { useAssets } from '../../../../../contexts/assets';

interface Props {
  nft: IFightNft;
  bet: number;
  next: (fightId: string) => void;
  data: WSFightData;
}

const BattleAction = ({ nft, bet, next, data }: Props) => {
  const { sound } = useAssets();
  const alert = useAlert();
  const { subscribeToEvent, makeMove /*, giveUp*/ } = useWsToHttp();
  const self = usePlayer(
    nftToPlayer(nft),
    !!data.movedWith,
    data.cards,
    data.movedWith
  );
  const opponent = usePlayer(
    data.opponent,
    data.opponentReady,
    data.opponentCards
  );
  const timer = useCountDown();
  const [timeLeft, setTimeLeft] = useState(data.timeLeft);
  const [roundResult, setRoundResult] = useState<RoundResults>();
  const leftAnimator = useGlbAnimations();
  const rightAnimator = useGlbAnimations();
  //const [currentRound, setCurrentRound] = useState(data.currentRound);
  //const [giveUpLoading, setGiveUpLoading] = useState(false);

  const [sizeX] = useWindowSize();

  const isTablet = useMemo(() => {
    return sizeX <= 1000;
  }, [sizeX <= 1000]);

  const isMobile = useMemo(() => {
    return sizeX <= 850;
  }, [sizeX <= 850]);

  const isUltraMobile = useMemo(() => {
    return sizeX <= 400;
  }, [sizeX <= 400]);

  useEffect(() => {
    init();
    subscribeOnRound();
    subscribeOnFightEnd();
    //sound.battleCry(0);
  }, []);

  const init = () => {
    timer.start(
      data.timeLeft,
      (left) => setTimeLeft(left),
      () => setTimeLeft(-1)
    );
  };

  const subscribeOnRound = () => {
    subscribeToEvent(wsActionTypes.server.fight.round.opponentReady).then(
      () => {
        opponent.setReady(true);
      }
    );
    subscribeToEvent<WSRoundResults>(
      wsActionTypes.server.fight.round.roundResults
    ).then((data) => {
      leftAnimator.byCard(data.movedWith, () => sound.battleMove(), 500);
      rightAnimator.byCard(data.opponentMovedWith, () => {
        setRoundResult(data.isWinner ? 'win' : data.draw ? 'draw' : 'lose');
        opponent.setMovedWith(data.opponentMovedWith);
      });
      timer.stop();
    });
    subscribeToEvent<WSRoundStart>(
      wsActionTypes.server.fight.round.roundStart
    ).then((data) => {
      setRoundResult(undefined);
      //setCurrentRound(data.currentRound);
      self.setCards(data.cards);
      self.setReady(false);
      self.setMovedWith(undefined);
      opponent.setCards(data.opponentCards);
      opponent.setReady(false);
      opponent.setMovedWith(undefined);
      leftAnimator.toShake();
      rightAnimator.toShake();
      timer.start(
        data.timeLeft,
        (left) => setTimeLeft(left),
        () => setTimeLeft(-1)
      );
      subscribeOnRound();
    });
  };

  const subscribeOnFightEnd = () => {
    subscribeToEvent<WSFightEnd>(wsActionTypes.server.fight.end).then(
      (data) => {
        timer.stop();
        next(data.fightId);
      }
    );
  };

  const handleMove = (card: CardNames) => {
    self.setReady(true);
    self.setMovedWith(card);
    makeMove(card).catch((err) => {
      alert.showError(err);
      self.setReady(false);
      self.setMovedWith(undefined);
    });
  };

  /*const handleGiveUp = () => {
    setGiveUpLoading(true);
    giveUp()
      .catch(() => {
        alert.showError(
          "Get up and fight! Something went wrong, can't give up"
        );
      })
      .finally(() => setGiveUpLoading(false));
  };*/

  return (
    <div className='fight-action'>
      <BattleActionHands
        leftColor={self.data.colors.hand}
        rightColor={opponent.data.colors.hand}
        leftAnimator={leftAnimator}
        rightAnimator={rightAnimator}
        size={
          isUltraMobile
            ? 'ultraSmall'
            : isMobile
            ? 'small'
            : isTablet
            ? 'middle'
            : 'big'
        }
      />
      <BattleActionInfo time={timeLeft} results={roundResult} />
      {isMobile ? (
        <BattleActionMobile
          self={self}
          opponent={opponent}
          handleMove={handleMove}
          bet={bet}
        />
      ) : isTablet ? (
        <BattleActionTablet
          self={self}
          opponent={opponent}
          handleMove={handleMove}
          bet={bet}
        />
      ) : (
        <BattleActionPC
          self={self}
          opponent={opponent}
          handleMove={handleMove}
          bet={bet}
        />
      )}
    </div>
  );
};

const nftToPlayer = (nft: IFightNft): WSPlayer => ({
  name: cutNftName(nft.name),
  image: nft.image,
  level: nft.level,
  rank: nft.rank,
  guild: nft.guild,
  colors: nft.colors
});

export default BattleAction;
